/**
 *
 * @param {object} obj 原始数据
 * @param {string} key1 键名
 * @param {string} key2  键名
 * @param {array?} keyOrder 排序数组
 * @returns {object} 指定的价值对对象
 */

import { $t } from '@/i18n'
import { getCurrencyCodeText } from '@/utils/tools'
import { reactive } from 'vue'

// 创建一个翻译代理工厂
const createAutoTMap = (baseMap) => {
  return new Proxy(baseMap, {
    get(target, key) {
      const value = target[key]
      if (value && typeof value === 'string') {
        // 使用 i18n 实例的 t 方法
        return $t(value)
      }
      return value
    }
  })
}

function objToSelectOptionsArray(
  obj,
  key1 = 'label',
  key2 = 'value',
  keyOrder
) {
  const arr = []
  if (keyOrder) {
    for (const key of keyOrder) {
      if (obj.hasOwnProperty(key)) {
        arr.push({
          [key1]: obj[key],
          [key2]: key
        })
      }
    }
  } else {
    for (const key in obj) {
      arr.push({
        [key1]: obj[key],
        [key2]: Number(key)
      })
    }
  }
  return arr
}

export const enabledMap = createAutoTMap({
  0: $t('common.disabled'),
  1: $t('common.enabled')
})
export const enabledOptions = objToSelectOptionsArray(enabledMap)

export const overdueMap = {
  0: '已超时',
  1: '未超时'
}
export const overdueOptions = objToSelectOptionsArray(overdueMap)

export const workOrderMap = {
  0: '待派单',
  1: '进行中',
  2: '服务中',
  3: '待商户确认',
  4: '服务完成'
}
export const workOrderOptions = objToSelectOptionsArray(workOrderMap)

/* 支付平台 / 平台 */
export const payTypeMap = createAutoTMap({
  // 0: $t('constant.unknown'),
  // 1: $t('constant.wx'),
  // 2: $t('constant.ali'),
  3: 'Pax',
  4: 'Wizar',
  5: 'Nayax',
  6: 'Castle',
  100: 'Antom',
  // 扫码购物钱包（订单 optPlat）：归属 Antom 平台，按钱包单独展示
  // 101: $t('constant.alipayHkWallet'),
  // 102: $t('constant.tngWallet')
  //TODO 海信暂无第三方支付平台
  // 3: '第三方'
})

export const payTypeOptions = objToSelectOptionsArray(payTypeMap)

// Antom 扫码钱包（订单 platform，即订单表 opt_plat）：100 支付平台下按钱包细分展示
export const antomScanWalletMap = {
  101: 'AlipayHK',
  102: "Touch 'n Go",
};

// 支付平台展示文案：Antom(100) 订单按 platform 追加钱包括号，如 "Antom (AlipayHK)"；
// 非 Antom 或 platform 缺失/未知时保持原映射（与旧行为一致）
export function formatPayType(payPlat, platform) {
  const name = payTypeMap[payPlat];
  if (Number(payPlat) === 100 && antomScanWalletMap[platform]) {
    return `${name} (${antomScanWalletMap[platform]})`;
  }
  return name;
}

// 购物终端/支付渠道品牌主色：取自各终端与钱包 App 的官方品牌色，用于图表按渠道区分颜色
export const payTypeColorMap = {
  3: '#1C3F94', // Pax
  4: '#E6002D', // Wizar
  5: '#7AB929', // Nayax
  6: '#0FA3A3', // Castle
  100: '#16A6F0', // Antom
  101: '#1677FF', // AlipayHK
  102: '#00A6E4' // Touch 'n Go
}

// 渠道无品牌色时的兜底配色（与历史饼图配色保持一致）
export const payTypeFallbackColors = ['#FF962B', '#8668FF', '#FF4C49', '#06D2D7']

export const cardTypeOptions = [
  { label: 'VISA', value: 'VISA' },
  { label: 'MASTERCARD', value: 'MASTERCARD' },
  { label: 'AMEX', value: 'AMEX' },
  { label: 'DISCOVER', value: 'DISCOVER' },
  { label: 'UNIONPAY', value: 'UNIONPAY' },
  { label: 'INTERAC', value: 'INTERAC' },
]

// 订单状态
export const orderStatusMap = createAutoTMap({
  0: $t('constant.pay'),
  1: $t('constant.payend'),
  2: $t('constant.payClose'),
  3: $t('constant.returnOk')
})
export const orderStatusOptions = objToSelectOptionsArray(orderStatusMap)

// 终端类型
export const terminalTypeMap = createAutoTMap({
  // 0: $t('constant.unknown'),
  // 1: $t('constant.miniProgram'),
  // 2: $t('constant.faceScan'),
  // 3: $t('constant.cardSwiping')
  3:$t('constant.POS'),
  4:$t('constant.H5'),
  //TODO 海信暂无第三方程序、刷掌购物终端
  // 4: '第三方程序',
  // 6: '刷掌'
})
export const terminalTypeOptions = objToSelectOptionsArray(terminalTypeMap)

// 结算状态
export const settleAccountsStatusMap = createAutoTMap({
  0: $t('constant.unsettled'),
  1: $t('constant.settled'),
});
export const settlementStatusMapOptions =
  objToSelectOptionsArray(settleAccountsStatusMap);

/* 订单类型 */
export const orderTypeMap = createAutoTMap({
  0: $t('constant.orderNormal'),
  1: $t('constant.orderCompensation'),
  2: $t('constant.orderAbnormal')
})
export const orderTypeOptions = objToSelectOptionsArray(orderTypeMap)

// 机型版本
export const machineVersionMap = createAutoTMap({
  1: $t('constant.machineVersionDemo'),
  2: $t('constant.machineVersionCommercial')
})
export const machineVersionOptions = objToSelectOptionsArray(machineVersionMap)

// 退款状态
export const refundStatusMap = createAutoTMap({
  1: $t('constant.refundPartial'),
  2: $t('constant.refundFull')
})
export const refundStatusOptions = objToSelectOptionsArray(refundStatusMap)

// 是否是0元订单
export const isZeroOrder = createAutoTMap({
  0: $t('constant.no'),
  1: $t('constant.yes')
})
export const isZeroOrderOptions = objToSelectOptionsArray(isZeroOrder)

// 是否标品
export const isStandardMap = createAutoTMap({
  0: $t('productModules.merchantGoods.standardProductN'),
  1: $t('productModules.merchantGoods.standardProductY')
})
export const isStandardOptions = objToSelectOptionsArray(isStandardMap)

// 审核状态
export const verifyMap = createAutoTMap({
  0: $t('common.reviewing'),
  1: $t('common.reviewing'),
  3: $t('constant.APPROVED'),
  2: $t('constant.REJECTED'),
  4: $t('constant.REJECTED'),
  5: $t('productModules.merchantGoods.withdrawn')
})
export const verifyOptions = objToSelectOptionsArray(verifyMap)

export const statisticalModeOptions = () => [
  {
    label: $t('constant.dailyReport'),
    value: '1'
  },
  {
    label: $t('constant.monthlyReport'),
    value: '2'
  },
  {
    label: $t('constant.quarterlyReport'),
    value: '4'
  },
  {
    label: $t('constant.annualReport'),
    value: '3'
  }
]

export const searchOrgOrShopMap = {
  0: '所属组织',
  1: '所属店铺'
}
export const searchOrgOrShopOptions =
  objToSelectOptionsArray(searchOrgOrShopMap)

// 商品来源
export const goodsSourceMap = {
  0: '商户商品库',
  1: '外采商品'
}

// 虚拟商品类型
export const virtualGoodsTypeMap = {
  0: '提货券',
  1: '尊享卡',
  2: '超值卡'
  // 3: '取件码'
}
export const virtualGoodsTypeOptions =
  objToSelectOptionsArray(virtualGoodsTypeMap)

// 虚拟商品状态
export const virtualGoodsStatusMap = {
  0: '已发布',
  1: '进行中',
  2: '已过期'
}
export const virtualGoodsStatusOptions = objToSelectOptionsArray(
  virtualGoodsStatusMap
)
//虚拟商品有效期
export const virtualGoodsValidityMap = {
  4: '当日有效',
  3: '1个月',
  0: '3个月',
  1: '6个月',
  2: '1年',
  5: '2年'
}
export const virtualGoodsValidityOptions = objToSelectOptionsArray(
  virtualGoodsValidityMap, 'label', 'value', [4, 3, 0, 1, 2, 5]
)

//虚拟商品作废状态
export const repealStatusMap = {
  0: '未作废',
  1: '已作废'
}
export const repealStatusOptions = objToSelectOptionsArray(
  repealStatusMap
)
// 店铺日志操作类型
export const operateTypeMap = {
  // 0: '新建店铺',
  // 1: '编辑店铺',
  0: $t('storeModules.operationLog.createStore'),
  1: $t('storeModules.operationLog.editStore'),
  13: $t('storeModules.store.importTemplate'),
  2: $t('productModules.generalTemplates.bulkOperation'),
  3: $t('productModules.pricingStrategy.bulkEdit'),
  4: $t('storeModules.operationLog.storeSettings'),
  6: $t('storeModules.operationLog.rebootDevice'),
  8: $t('merchandisingModules.storeInventory.generateRefillOrder'),
  9: $t('merchandisingModules.storeInventory.exportRefillOrder'),
  10: $t('merchandisingModules.storeInventory.thresholdSettings'),
  11: $t('navModules.generalTemplates'),
  15: $t('storeModules.operationLog.power-onState'),
  16: $t('storeModules.operationLog.editProduct'),
  17: $t('storeModules.operationLog.replaceProduct'),
  18: $t('storeModules.operationLog.deleteProduct'),
  20: $t('storeModules.operationLog.oneClickOpenDoor'),
  21: $t('storeModules.operationLog.oneClickSync'),
  22: $t('storeModules.operationLog.deleteStore'),
  // 19: '厂商限价调整'
  // 12: '店铺复制'
}
export const operateTypeOptions = objToSelectOptionsArray(operateTypeMap)

// 店铺日志来源
export const sourceMap = {
  0: 'PC-operator',
  1: 'APP',
  2: 'PC-admin',
}
export const sourceOptions = objToSelectOptionsArray(sourceMap)

// 活动类型
export const promotionTypeMap = createAutoTMap({
  1: $t('constant.instantDiscount'),
  // 2: '优惠折扣',
  3: $t('constant.productDiscounts'),
  // 4: '随机立减',
  5: $t('constant.ordersDiscount'),
  9: $t('orderModules.salesOrder.butABenefitB')
})
export const promotionTypeOptions = objToSelectOptionsArray(promotionTypeMap)

// 活动状态
export const marketingStatusMap = {
  0: '禁用',
  1: '进行中',
  2: '过期',
  3: '关闭'
}
export const marketingStatusOptions =
  objToSelectOptionsArray(marketingStatusMap)

// 活动操作类型
export const markOperationTypeMap = {
  0: '禁用',
  1: '启用',
  2: '新建'
}
export const markOperationTypeOptions = objToSelectOptionsArray(markOperationTypeMap)

// 广告位
export const locationMap = createAutoTMap({
  1: 'adManage.constant.adScreen',
  // 2: 'adManage.constant.faceScreen',
  // 3: 'adManage.constant.shoppingSide',
  4: 'adManage.constant.posScreen'
})
export const advertisingPosition = objToSelectOptionsArray(locationMap)

export const adsTypeMap = createAutoTMap({
  0: 'adManage.constant.imageSet',
  1: 'adManage.constant.video'
})

export const adsVerifiedType = createAutoTMap({
  0: 'adManage.constant.pendingReview',
  1: 'adManage.constant.approved',
  2: 'adManage.constant.rejected'
})

export const adsVerifiedTypeOptions = objToSelectOptionsArray(adsVerifiedType)

// 广告状态
export const statusPlanMap = createAutoTMap({
  0: 'adManage.constant.stopped',
  1: 'adManage.constant.playing',
  2: 'adManage.constant.expired'
})
export const statusPlanOptions = objToSelectOptionsArray(statusPlanMap)

/* 退款原因 */
export const applyReasonMap = createAutoTMap({
  0: $t('constant.other'),
  1: $t('constant.mistakenDetention'),
  2: $t('constant.packagingDamaged'),
  3: $t('constant.productExpired'),
  // 4: $t('constant.notParticipatePromotion'),
  // 5: $t('constant.wrongIdentification')
})

export const applyReasonMapOptions = objToSelectOptionsArray(applyReasonMap)

// 异常订单列表类型
export const exceptionTypeMap = createAutoTMap({
  0: $t('constant.noException'),
  1: $t('constant.noFriendly'),
  2: $t('constant.videoErr'),
  // 2: '图像模糊/无图像',
  3: $t('constant.goodsNo'),
  4: $t('constant.foreignBody'),
  5: $t('constant.viewErr')
})
export const exceptionTypeOptions = objToSelectOptionsArray(exceptionTypeMap)

// 异常订单查询类型
export const exceptionSearchTypeMap = createAutoTMap({
  1: $t('constant.noFriendly'),
  2: $t('constant.videoErr'),
  // 2: '图像模糊/无图像',
  3: $t('constant.goodsNo'),
  4: $t('constant.foreignBody'),
  5: $t('constant.viewErr')
})
export const exceptionSearchTypeOptions = objToSelectOptionsArray(exceptionSearchTypeMap)

// 运行状态
export const runStatusMap = {
  0: '离线',
  1: '在线'
}
export const runStatusOptions = objToSelectOptionsArray(runStatusMap)
// 店铺看板页面运行状态

export const shopSignRunOptions = objToSelectOptionsArray({
  ...runStatusMap,
  '': '全部'
})

// 店铺状态
export const shopEnabledMap = createAutoTMap({
  0: $t('common.disabledA'), 
  1: $t('common.enabledA')
})
export const shopEnabledOptions = objToSelectOptionsArray(shopEnabledMap)

// 制冷设置
export const temperatureMap = {
  2: '2°C ~ 6°C',
  3: '3°C ~ 7°C',
  4: '4°C ~ 8°C',
  5: '5°C ~ 9°C',
  6: '6°C ~ 10°C'
}
export const temperatureOptions = objToSelectOptionsArray(temperatureMap)

// 华氏制冷设置
export const fahrenheitTemperatureMap = {
  2: '35.6°F ~ 42.8°F',
  3: '37.4°F ~ 44.6°F',
  4: '39.2°F ~ 46.4°F',
  5: '41.0°F ~ 48.2°F',
  6: '42.8°F ~ 50.0°F'
}
export const fahrenheitTemperatureOptions = objToSelectOptionsArray(fahrenheitTemperatureMap)

// 用户类型
export const userTypeMap = createAutoTMap({
  0: $t('constant.userNew'),
  1: $t('constant.userOld')
})
export const userTypeOptions = objToSelectOptionsArray(userTypeMap)

// 操作日志订单类型
export const logOrderTypeMap = createAutoTMap({
  0: $t('navModules.orderManageSalesOrder'),
  1: $t('navModules.orderManageAfterOrder'),
  2: $t('navModules.orderManageExceptionOrder')
})
export const logOrderTypeOptions = objToSelectOptionsArray(logOrderTypeMap)

// 补货状态
export const generateMap = createAutoTMap({
  1: $t('merchandisingModules.storeInventory.generated'),
  0: $t('merchandisingModules.storeInventory.notGenerated'),
})
export const generateOptions = objToSelectOptionsArray(generateMap)

// 单 双门
export const binTypeMap = createAutoTMap({
  0: $t('constant.singleDoor'),
  1: $t('constant.doubleDoor')
})
export const binTypeOptions = objToSelectOptionsArray(binTypeMap)

// 缺货阈值
export const outStockThresholdMap = createAutoTMap({
  0: $t('merchandisingModules.storeInventory.percentage'),
  1: $t('merchandisingModules.storeInventory.remainingInventory'),
  2: $t('merchandisingModules.storeInventory.topSales'),
})
export const outStockThresholdOptions =
  objToSelectOptionsArray(outStockThresholdMap)

// 库存状态
export const stockStatusMap = createAutoTMap({
  0: $t('constant.normal'),
  1: $t('constant.outStock'),
})
export const stockStatusOptions = objToSelectOptionsArray(stockStatusMap)

// 补货单类型
export const replenishmentTypeMap = {
  0: '单店铺',
  1: '汇总'
}
export const replenishmentTypeOptions =
  objToSelectOptionsArray(replenishmentTypeMap)

// 柜体
export const cabinetMap = createAutoTMap({
  0: $t('constant.singleDoor'),
  1: $t('constant.leftCabinet'),
  2: $t('constant.rightCabinet')
})
export const cabinetOptions = objToSelectOptionsArray(cabinetMap)

// 理货类型
export const tallyTypeMap = createAutoTMap({
  0: $t('constant.saleSupp'),
  1: $t('constant.delivery'),
  2: $t('constant.inventoryCount'),
  3: $t('constant.refund')
})
export const tallyTypeOptions = objToSelectOptionsArray(tallyTypeMap)

// 图片审核状态
export const urlExamineStatusMap = createAutoTMap({
  0: $t('constant.unreviewed'),
  1: $t('constant.reviewPass'),
  2: $t('constant.reviewFailed')
})
// 开门状态
export const doorStatusMap = createAutoTMap({
  0: $t('constant.no'),
  1: $t('constant.yes')
})

// 盘点类型
export const inventoryTallyTypeMap = createAutoTMap({
  0: $t('merchandisingModules.merchOrderSearch.doorOpenInventory'),
  1: $t('merchandisingModules.merchOrderSearch.remoteInventory')
})

// 变更类型
export const changeTypeMap = createAutoTMap({
  0: $t('constant.sale'),
  1: $t('constant.shelved'),
  2: $t('constant.delisted')
})
export const changeTypeOptions = objToSelectOptionsArray(changeTypeMap)

// 卡券类型
export const couponManagerTypeMap = createAutoTMap({
  1: $t('orderModules.salesOrder.cashVoucher'),
  2: $t('orderModules.salesOrder.fullDiscount'),
  3: $t('orderModules.salesOrder.discountCoupons'),
  4: $t('orderModules.salesOrder.discountCoupon')
})
export const couponManagerTypeOptions =
  objToSelectOptionsArray(couponManagerTypeMap)

// 卡券状态
export const couponManagerEnabledMap = {
  0: '禁用',
  1: '启用',
  2: '已过期'
}
export const couponManagerEnabledOptions = objToSelectOptionsArray(
  couponManagerEnabledMap
)

// 结算中心-交易明细-财务类型
export const platFinanceTypeMap = createAutoTMap({
  2001: $t('constant.orderMoney'),
  3001: $t('constant.algorithmServiceFee'),
  3002: $t('constant.platformServiceFee'),
  3003: $t('constant.simCardServiceFee'),
  3004: $t('constant.wifiServiceFee'),
  2002: $t('constant.refund'),
  // 14: '支付宝通道费收入',z
  // 3: $t('constant.algorithmBasedSettlement'),
  // 4: $t('constant.otherExpenses')
})
export const platFinanceTypeOptions =
  objToSelectOptionsArray(platFinanceTypeMap)

// 结算中心-交易明细-交易类型
export const platTradeTypeMap = createAutoTMap({
  1: $t('constant.revenue'),
  2: $t('constant.expenses')
})
export const platTradeTypeOptions =
  objToSelectOptionsArray(platTradeTypeMap)

// 支付类型
export const paymentTypeMap = {
  1: '微信',
  2: '支付宝'
}
export const paymentTypeOptions = objToSelectOptionsArray(paymentTypeMap)

// 提现类型
export const accountTypeMap = {
  0: '对私账户',
  1: '对公账户',
  2: '支付宝',
  3: '微信'
}
export const accountTypeOptions = objToSelectOptionsArray(accountTypeMap)

// 账户明细提现状态
export const withdrawalDetailStatusMap = {
  0: '提现中',
  1: '成功',
  2: '失败'
}
export const withdrawalDetailStatusOptions = objToSelectOptionsArray(
  withdrawalDetailStatusMap
)

// 费用中心-转入方式
export const incomeFinanceTypeMap = {
  3: '手动充值',
  // 4: '余额转入',
  11: '支付宝分账',
  12: '微信分账'
}
export const incomeFinanceTypeOptions =
  objToSelectOptionsArray(incomeFinanceTypeMap)

// 费用中心-费用明细-费用类型(自收)
export const expenseTypeMap = createAutoTMap({
  3001: $t('constant.algorithmServiceFee'),
  3002: $t('constant.platformServiceFee'),
  3003: $t('constant.simCardServiceFee'),
  3004: $t('constant.wifiServiceFee'),
})
export const expenseTypeOptions = objToSelectOptionsArray(
  expenseTypeMap
)

// 费用中心-费用明细-费用类型(代收)
export const expenseTypeAgentMap = createAutoTMap({
  // 4000: $t('constant.payChannelFee'),
  3001: $t('constant.algorithmServiceFee'),
  3002: $t('constant.platformServiceFee'),
  3003: $t('constant.simCardServiceFee'),
  3004: $t('constant.wifiServiceFee'),
})
export const expenseTypeAgentOptions = objToSelectOptionsArray(
  expenseTypeAgentMap
)

// 费用中心-费用管理-费用类型
export const expenseServiceTypeMap = {
  // 0: '未知',
  // 1: '算法服务费',
  // 2: '低产折旧费',
  // 9: '流量卡费',
  // 10: '订单催收费',
  // 13: '手机号验证费',
  // 14: '短信费'
  1: $t('constant.algorithmServiceFee'),
  2: '平台服务费',
  3: 'SIM卡服务费',
  10: '订单催收费',
  13: '手机号验证费'
  // 14: '平台渠道费'
}
export const expenseServiceTypeOptions = objToSelectOptionsArray(
  expenseServiceTypeMap
)

// 费用中心识别方案
export const expenseIntelliTypeMap = {
  0: '视觉识别',
  1: '重力识别',
  2: '视觉重力识别',
  3: 'RFID识别'
}
export const expenseIntelliTypeOptions = objToSelectOptionsArray(
  expenseIntelliTypeMap
)

// 费用中心-费用明细-结算状态
export const expenseStatusMap = createAutoTMap({
  0: $t('constant.unsettled'),
  1: $t('constant.settled'),
})
export const expenseStatusOptions = objToSelectOptionsArray(
  expenseStatusMap
)

// 货柜类型
export const cabinetIntelliTypeMap = {
  0: '视觉柜',
  1: '重力柜',
  2: '视觉重力柜',
  3: 'rfid柜'
}
export const cabinetIntelliTypeOptions = objToSelectOptionsArray(
  cabinetIntelliTypeMap
)

export const affiliationTypeMap = {
  0: '运营商',
  1: '服务商'
}
export const affiliationTypeOptions =
  objToSelectOptionsArray(affiliationTypeMap)

// SIM用途
export const simPurposeMap = {
  0: '自用',
  1: '货柜',
  2: '刷脸'
}
export const simPurposeOptions = objToSelectOptionsArray(simPurposeMap)

// 已开票/待开票 服务费类型
export const invoicedMap = {
  1: $t('constant.algorithmServiceFee'),
  2: $t('constant.platformServiceFee'),
  3: $t('constant.simCardServiceFee') + getCurrencyCodeText(true),
  // 10: '订单催收服务费',
  // 13: '手机号验证服务费'
  0:'分期合同'
}
export const invoicedOptions = objToSelectOptionsArray(invoicedMap)

// 开票状态
export const invoicedStatusMap = {
  1: '待开票',
  2: '已开票',
  3: '开票中'
}
export const invoicedStatusOptions = objToSelectOptionsArray(invoicedStatusMap)

// 合伙人类型
export const partnerTypeMap = {
  0: '运营商',
  1: '合作方',
  3: '算法商'
}

export const partnerTypeOptions = objToSelectOptionsArray(partnerTypeMap)

// 分润形式
export const dividendFormMap = {
  0: '系统清分',
  1: '平台清分'
}
// 提现方式
export const withdrawMethodMap = {
  0: '对私银行',
  1: '对公银行'
}
export const withdrawMethodOptions = objToSelectOptionsArray(withdrawMethodMap)

// 销售订单商品状态  goods_status
export const orderGoodsStatusMap = createAutoTMap({
  // 0: $t('constant.payend'),
  // 1: $t('constant.returning'),
  // 2: $t('constant.returned'),
  // 3: $t('constant.refuseRefund'),
  // 4: $t('constant.payNot'),
  // 5: $t('constant.closed')
  0: $t('constant.payNot'),
  1: $t('constant.payend'),
  2: $t('constant.closed'),
  11: $t('constant.returning'),
  12: $t('constant.refundProcessing'),
  13: $t('constant.refuseRefund'),
  14: $t('constant.refundFailed'),
  15: $t('constant.returned')
})
export const orderGoodsStatusOptions =
  objToSelectOptionsArray(orderGoodsStatusMap)

// 结算方式
export const settlementStatusMap = {
  0: '待处理',
  1: '成功',
  2: '失败'
}
export const settlementStatusOptions =
  objToSelectOptionsArray(settlementStatusMap)

// 结算方式
export const clearingFormMap = {
  0: '手动结算',
  1: '自动结算'
}
export const clearingFormOptions = objToSelectOptionsArray(clearingFormMap)

// 双开门类型
export const doubleDoorTypeMap = {
  0: '单开门',
  1: {
    0: '左右门',
    1: '左门',
    2: '右门'
  }
}

// 分润方式
export const profitTypeMap = {
  0: '百分比分润',
  1: '单笔分润'
}
export const profitTypeOptions = objToSelectOptionsArray(profitTypeMap)

// 分润状态
export const profitstate = {
  0: '已禁用',
  1: '已启用'
}
export const profitStateOptions = objToSelectOptionsArray(profitstate)

// 电源状态
export const powerSupplyMap = createAutoTMap({
  0: $t('storeModules.store.battery'),
  1: $t('storeModules.store.mainsPower')
})
export const powerSupplyOptions = objToSelectOptionsArray(powerSupplyMap)

// 设备厂商
export const deviceFirmMap = {
  SY: '智佳',
  HR: '海容',
  HCK: '哈士奇',
  HX: '海信'
}
export const deviceFirmOptions = objToSelectOptionsArray(deviceFirmMap)
// 识别状态
export const recognitionStatusMap = createAutoTMap({
  0: $t('constant.recognizing'),
  1: $t('constant.recognized')
})

// 订单状态
export const orderCloseMap = createAutoTMap({
  0: $t('constant.closeOrder'),
  1: $t('constant.offlineClose')
})
export const orderCloseOptions = objToSelectOptionsArray(orderCloseMap)

// 用户启用禁用状态
export const userEnabledMap = createAutoTMap({
  0: $t('common.disabled'),
  1: $t('common.enabled')
})
export const userEnabledOptions = objToSelectOptionsArray(userEnabledMap)

// 货柜操作类型
export const cabinetBindTypeMap = {
  1: '绑定',
  0: '解绑'
}
export const cabinetBindTypeOptions =
  objToSelectOptionsArray(cabinetBindTypeMap)
// 订单退款状态
export const RefundTypeMap = createAutoTMap({
  0: $t('orderModules.salesOrder.singleRefund'),
  1: $t('orderModules.salesOrder.refundDifference')
})

export const refundReasonTypeMap = createAutoTMap({
  1: $t('constant.mistakenDetention'),
  2: $t('constant.packagingDamaged'),
  3: $t('constant.productExpired'),
  4: $t('constant.notParticipatePromotion'),
  5: $t('constant.wrongIdentification'),
  0: $t('constant.other')
})

// 禁用原因
export const disableReasonMap = createAutoTMap({
  0: $t('constant.foreignBody'),
  1: $t('constant.cameraObstruction'),
  2: $t('constant.other')
})
export const disableReasonOptions = objToSelectOptionsArray(disableReasonMap)

// 第n次申请
export const applyCountMap = {
  1: '一次申请',
  2: '二次申请'
}

export const informTypeMap = createAutoTMap({
  0: $t('noticeModules.tab.all'),
  1: $t('noticeModules.tab.business'),
  2: $t('noticeModules.tab.sysMaintenance'),
  3: $t('noticeModules.tab.error'),
  4: $t('noticeModules.tab.repair')
})
export const informTypeOptions = objToSelectOptionsArray(informTypeMap)

export const ExceptionNoticeTypeMap = createAutoTMap({
  1: $t('constant.goodsException'),
  2: $t('constant.orderException')
})

// 品牌方活动
export const brandPromotionTypaMap = {
  1: '首单立减',
  2: '优惠折扣',
  3: '满减券',
  4: '递减券',
  5: '新品N元',
  6: '订单金额满减券',
  7: '订单数量满减券',
  8: '订单满折券',
  9: '买A惠B'
}

// 订单退款明细状态
export const orderRefundMap = createAutoTMap({
  // 0: '申请退款',
  1: $t('constant.returning'),
  2: $t('constant.refundProcessing'),
  3: $t('constant.refuseRefund'),
  4: $t('constant.refundFailed'),
  5: $t('constant.returned')
})
export const orderRefundOptions = objToSelectOptionsArray(orderRefundMap)

// 异常订单状态
export const exceptionOrderStatusMap = createAutoTMap({
  0: $t('TODO.pending'),
  1: $t('constant.pended'),
  2: $t('constant.payClose')
})
export const exceptionOrderStatusOptions = objToSelectOptionsArray(
  exceptionOrderStatusMap
)

// 是否是超管
export const isRootMap = createAutoTMap({
  0: $t('constant.no'),
  1: $t('constant.yes')
})
export const isRootOptions = objToSelectOptionsArray(createAutoTMap)

export const markTypeMap = createAutoTMap({
  1: $t('constant.videoFrameDrops'),
  2: $t('constant.cameraObstruction'),
  3: $t('constant.videoExposure'),
  4: $t('constant.openDoorOffPower'),
  5: $t('constant.longTimeRecording'),
  6: $t('constant.greenScreen'),
  7: $t('constant.productOverlay'),
  8: $t('constant.otherReasons')
})
export const markTypeOptions = objToSelectOptionsArray(markTypeMap)

// 断品状态
// 策略状态
export const isWarningMap = {
  0: '未预警',
  1: '已预警'
}
export const isWarningOptions = objToSelectOptionsArray(isWarningMap)

// 策略状态
export const PolicyStateMap = createAutoTMap({
  0: $t('common.disabled'),
  1: $t('common.enabled')
  
})
export const PolicyStateOptions = objToSelectOptionsArray(PolicyStateMap)

// 投诉类型
export const customerTypeMap = {
  1: '出货后未进行扣费',
  2: '出货商品与选购不一致',
  3: '订单无法付款',
  4: '对商品定价有争议',
  5: '扣费金额不对',
  6: '扣费金额与标价不一致',
  7: '扣费商品与实际取走的不符',
  8: '扣费与实际出货不符',
  9: '商品已过期',
  10: '商品质量有问题',
  11: '实际并未取走商品但被扣费',
  12: '柜门无法打开',
  13: '关闭进行中订单',
  0: '其他'
}
export const customerTypeOptions = objToSelectOptionsArray(customerTypeMap)

// 异常名称
export const abnormalNameMap = {
  1001: '摄像头异常',
  1002: '门异常',
  1003: '锁异常',
  1004: '温度传感器异常',
  1005: '存储空间不足',
  1006: '订单推送堵塞',
  1007: '开锁失败',
  1008: '关门失败',
  1009: '视频录制超长',
  1010: '视频上传积压',
  1011: '交易中断电',
  1012: '摄像头缺失',
  1013: '设备异常移动'
}
export const abnormalNameOptions = objToSelectOptionsArray(abnormalNameMap)

// 异常说明
export const abnormalMarkMap = {
  1001: '摄像头打开录制失败',
  1002: '非交易状态下，门开锁关',
  1003: '非交易状态下，锁开门关',
  1004: '支持读取温度的设备无法读取到温度',
  1005: '存储空间低于400兆',
  1006: '当前设备存在30个订单未上报',
  1007: '订单开锁失败',
  1008: '订单无法关门',
  1009: '单次购物超过20min',
  1010: '当前设备超过30个订单视频未上传',
  1011: '订单开门交易中突然断电，导致订单数据异常',
  1012: '设备检测到的摄像头个数和出厂个数不一致',
  1013: '设备定位发生巨大偏移'
}

// 微信/支付宝 收款账户状态
export const collectionPaymentMap = {
  0: '未提交',
  1: '审核中',
  2: '待确认',
  3: '待入驻',
  4: '绑定中',
  5: '不通过',
  6: '已入驻'
}


// 主体类型
export const mainTypeMap = {
  0: '个人',
  1: '个体户',
  2: '企业',
  3: '其他组织'
}

export const adventType = createAutoTMap({
  0: $t('productModules.nearExpiryProduct.notYetDue'),
  1: $t('productModules.nearExpiryProduct.expiredDate'),
  2: $t('productModules.nearExpiryProduct.expired')
})


//开票方式
export const invoiceType = {
  0: '单独开票',
  1: '合并开票'
}

export const ReceiveTypeMap = createAutoTMap({
  0: $t('constant.QFHarvest'),
  1: $t('constant.shopHarvest'),
  2: $t('constant.manufacturerHarvest')
})

export const complaintType = {
  0: '待处理',
  1: '处理中',
  2: '已处理'
}

//算法对账-费用明细-费用状态
export const algorithmExpenseStatus = {
  0: '未结算',
  1: '已结算',
  2: '仅记账'
}

// 合同状态
export const contractMap = createAutoTMap({
  1: $t('constant.active'),
  2: $t('common.reviewing'),
  3: $t('constant.overdue'),
  4: $t('constant.completed'),
  5: $t('constant.rejectedNew'),
  6: $t('constant.toBeConfirmed'),
  7: $t('constant.canceled')
})
export const contractOptions = objToSelectOptionsArray(contractMap)

// 合同还款方式
export const contractRepaymentTypeMap = createAutoTMap({
  0: $t('constant.unknown'),
  1: $t('settlementCenter.installment.autoRepayment'),
  2: $t('settlementCenter.installment.manualCorporateTransfer')
})
export const contractRepaymentTypeOptions = objToSelectOptionsArray(contractRepaymentTypeMap)
// 合同还款方式含全部
export const contractRepaymentTypeAllMap = createAutoTMap({
  0: $t('common.all'),
  1: $t('settlementCenter.installment.autoRepayment'),
  2: $t('settlementCenter.installment.manualCorporateTransfer')
})
export const contractRepaymentTypeAllOptions = objToSelectOptionsArray(contractRepaymentTypeAllMap)

// 合同还款方式
export const contractRepaymentModeMap = createAutoTMap({
  0: $t('constant.unknown'),
  1: $t('settlementCenter.installment.currentPayment'),
  2: $t('settlementCenter.installment.payOff')
})
export const contractRepaymentModeOptions = objToSelectOptionsArray(contractRepaymentModeMap)

// 进件申请状态
export const applyStatusMap = {
  0: $t('infeed.wtg'),
  1: $t('infeed.dqr'),
  2: $t('infeed.yrz')
};
export const applyStatusOptions = objToSelectOptionsArray(applyStatusMap);

// 商户类型
export const subMerTypeMap = {
  0: $t('infeed.gr'),
  1: $t('infeed.gth'),
  2: $t('infeed.qy'),
};
export const subMerTypeOptions = objToSelectOptionsArray(subMerTypeMap);
